/**
 * J10 · Localization framework: English at launch, German first added locale.
 * `t('key', 'English fallback')` — untranslated keys render the fallback, so
 * pages can ship English-first and pick up German as the dictionary grows.
 */

import de from './locales/de.json';

const dictionaries: Record<string, Record<string, string>> = { de };

let currentLocale = localStorage.getItem('solfo.locale') ?? 'en';

export function setLocale(locale: string) {
  currentLocale = locale;
  localStorage.setItem('solfo.locale', locale);
}

export function getLocale(): string {
  return currentLocale;
}

export function t(key: string, fallback: string): string {
  if (currentLocale === 'en') return fallback;
  return dictionaries[currentLocale]?.[key] ?? fallback;
}

// ── Exercise content localization (J10) ─────────────────────────────────────
// Catalog exercises carry translations in `localizedJson`
// ({"de": {name, description}}); coach-authored exercises usually don't and
// fall back to their authored text.

export interface LocalizableExercise {
  name: string;
  description?: string | null;
  localizedJson?: string | null;
}

function localized(ex: LocalizableExercise): { name?: string; description?: string } {
  if (!ex.localizedJson || currentLocale === 'en') return {};
  try {
    const map = JSON.parse(ex.localizedJson) as Record<string, { name?: string; description?: string }>;
    return map[currentLocale] ?? {};
  } catch {
    return {};
  }
}

export function exName(ex: LocalizableExercise | null | undefined): string {
  if (!ex) return '…';
  return localized(ex).name ?? ex.name;
}

export function exDesc(ex: LocalizableExercise | null | undefined): string {
  if (!ex) return '';
  return localized(ex).description ?? ex.description ?? '';
}
